import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTrafficQuality } from '@/api/hooks';
import { useAgent } from '@/auth/use-agent';
import type { TrafficQualityData } from '@/api/types';
import { ErrorNotice } from '@/ui/ErrorNotice';
import { Loading } from '@/ui/Loading';
import { Notice } from '@/ui/Notice';
import { dashboardDocument } from './dashboard-document';

// Below this the report's filter bar and first table do not fit; the page scrolls instead.
const MIN_HEIGHT_PX = 560;

/**
 * The report fills the content area edge to edge, from under the header to the bottom of the
 * window, and scrolls inside itself like the standalone file does. The sandbox gives it scripts
 * and the CSV download only: it runs on an opaque origin, so it cannot read the agent key.
 */
function Report({ data }: { data: TrafficQualityData }) {
  const srcDoc = useMemo(() => dashboardDocument(data), [data]);
  const box = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const fit = () => {
      const top = (box.current?.getBoundingClientRect().top ?? 0) + window.scrollY;
      setHeight(Math.max(MIN_HEIGHT_PX, Math.floor(window.innerHeight - top)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);

  return (
    <div ref={box} className="-mx-6 -my-6 bg-[#0F1319]">
      <iframe
        title="Traffic quality"
        srcDoc={srcDoc}
        sandbox="allow-scripts allow-downloads"
        className="block w-full border-0"
        style={{ height }}
      />
    </div>
  );
}

export function TrafficQualityPage() {
  const { agent } = useAgent();
  // Hooks run before the admin check; the query itself stays off for an agent.
  const report = useTrafficQuality(agent.admin);

  if (!agent.admin) return <Notice>Traffic quality is for admins only.</Notice>;
  if (report.isPending) return <Loading what="traffic quality" />;
  if (report.error) return <ErrorNotice error={report.error} />;
  if (!report.data) {
    return <Notice>No traffic quality data yet: the first nightly snapshot has not been copied over.</Notice>;
  }
  return <Report data={report.data} />;
}
